// src/components/StaffingFirms.tsx
//
// HR / Staffing firm directory page.
// Users browse firms that offer CV writing, cover letter help, interview prep,
// recruitment placement, salary negotiation, LinkedIn optimization, and
// career coaching. Clicking "Connect" records interest and opens the firm's
// website (or contact email) in a new tab.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";

type Firm = {
  id: string;
  slug: string;
  name: string;
  description: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  services: string[];
  industries: string[];
  regions: string[];
  isVerified: boolean;
  rating: number | null;
};

type Connection = {
  id: string;
  firmId: string;
  status: string;
  createdAt: string;
};

const SERVICE_LABELS: Record<string, { label: string; icon: string }> = {
  cv_building: { label: "CV / Resume building", icon: "📄" },
  cover_letter: { label: "Cover letter writing", icon: "✉️" },
  interview_prep: { label: "Interview prep", icon: "🎤" },
  recruitment: { label: "Recruitment placement", icon: "🤝" },
  career_coaching: { label: "Career coaching", icon: "🧭" },
  salary_negotiation: { label: "Salary negotiation", icon: "💰" },
  linkedin_optimization: { label: "LinkedIn optimization", icon: "🔗" },
};

const SERVICE_FILTERS = Object.entries(SERVICE_LABELS).map(([key, v]) => ({ key, ...v }));

const StaffingFirms: React.FC = () => {
  const navigate = useNavigate();
  const [firms, setFirms] = useState<Firm[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);

  // Initial load: firms + my prior connections
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get("/staffing-firms"),
      axiosInstance.get("/staffing-firms/my/connections").catch(() => ({ data: { connections: [] } })),
    ])
      .then(([firmsRes, connRes]) => {
        if (cancelled) return;
        setFirms(firmsRes.data.firms || []);
        setConnections(connRes.data.connections || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load staffing firms");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const connectedFirmIds = useMemo(() => new Set(connections.map((c) => c.firmId)), [connections]);

  const visible = useMemo(() => {
    return firms.filter((f) => {
      if (serviceFilter && !f.services.includes(serviceFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!f.name.toLowerCase().includes(q) && !f.description.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [firms, search, serviceFilter]);

  const handleConnect = async (firm: Firm) => {
    setConnecting(firm.id);
    try {
      const res = await axiosInstance.post(`/staffing-firms/${firm.id}/connect`);
      toast.success(`Connection recorded with ${firm.name}`);
      setConnections((prev) => {
        if (prev.find((c) => c.firmId === firm.id)) return prev;
        return [{ id: res.data.connection.id, firmId: firm.id, status: "requested", createdAt: new Date().toISOString() }, ...prev];
      });

      // Open the firm's website or email in a new tab so the user can take action
      const next = res.data.next;
      if (next?.websiteUrl) {
        window.open(next.websiteUrl, "_blank", "noopener,noreferrer");
      } else if (next?.contactEmail) {
        window.location.href = `mailto:${next.contactEmail}?subject=${encodeURIComponent("Interest from Sumbitrbuddy")}`;
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || "Failed to record connection");
    } finally {
      setConnecting(null);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          background: "transparent",
          border: "1px solid #cbd5e1",
          color: "#475569",
          borderRadius: 6,
          padding: "6px 14px",
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 13,
          marginBottom: 20,
        }}
      >
        ← Back to Dashboard
      </button>

      <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
        🤝 Connect with HR / Staffing firms
      </h1>
      <p style={{ margin: "0 0 24px", color: "#475569", lineHeight: 1.6 }}>
        Browse vetted recruitment and career-services firms. Get hands-on help with your CV, cover letter, interview prep,
        salary negotiation, and more — directly from people who do this for a living.
      </p>

      {/* Search + service filter row */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <input
          type="search"
          placeholder="Search firms by name or service..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search staffing firms"
          style={{
            padding: "10px 14px",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            fontSize: 14,
            minWidth: 260,
            flex: 1,
          }}
        />
        <button
          onClick={() => setServiceFilter(null)}
          style={chipStyle(serviceFilter === null)}
        >
          All services ({firms.length})
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        {SERVICE_FILTERS.map((s) => {
          const count = firms.filter((f) => f.services.includes(s.key)).length;
          return (
            <button
              key={s.key}
              onClick={() => setServiceFilter(serviceFilter === s.key ? null : s.key)}
              disabled={count === 0}
              style={chipStyle(serviceFilter === s.key)}
            >
              <span aria-hidden="true" style={{ marginRight: 4 }}>{s.icon}</span>
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Firm cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading firms...</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 8 }}>
          No firms match your filters. Try clearing them.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
          {visible.map((firm) => {
            const isConnected = connectedFirmIds.has(firm.id);
            return (
              <li
                key={firm.id}
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {firm.logoUrl ? (
                    <img src={firm.logoUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />
                  ) : (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        background: "linear-gradient(135deg, #6366f1, #1d4ed8)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 18,
                      }}
                    >
                      {firm.name.charAt(0)}
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{firm.name}</h3>
                      {firm.isVerified && (
                        <span
                          title="Verified by Sumbitrbuddy"
                          style={{ color: "#16a34a", fontSize: 14 }}
                          aria-label="Verified by Sumbitrbuddy"
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    {firm.rating !== null && (
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        ★ {firm.rating.toFixed(1)} avg rating
                      </div>
                    )}
                  </div>
                </div>

                <p style={{ margin: 0, fontSize: 13, color: "#475569", lineHeight: 1.5, flex: 1 }}>
                  {firm.description}
                </p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {firm.services.map((s) => (
                    <span
                      key={s}
                      style={{
                        fontSize: 11,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        padding: "2px 8px",
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                      title={SERVICE_LABELS[s]?.label}
                    >
                      {SERVICE_LABELS[s]?.icon} {SERVICE_LABELS[s]?.label || s}
                    </span>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                  <button
                    onClick={() => handleConnect(firm)}
                    disabled={connecting === firm.id}
                    style={{
                      flex: 1,
                      background: isConnected ? "#f1f5f9" : "#1d4ed8",
                      color: isConnected ? "#475569" : "#fff",
                      border: isConnected ? "1px solid #cbd5e1" : "none",
                      borderRadius: 6,
                      padding: "10px 16px",
                      fontWeight: 700,
                      cursor: connecting === firm.id ? "wait" : "pointer",
                      fontSize: 14,
                    }}
                  >
                    {connecting === firm.id ? "Connecting..." : isConnected ? "✓ Connected · open site" : "🤝 Connect"}
                  </button>
                  {firm.websiteUrl && (
                    <a
                      href={firm.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "transparent",
                        border: "1px solid #cbd5e1",
                        color: "#475569",
                        borderRadius: 6,
                        padding: "8px 12px",
                        fontWeight: 600,
                        fontSize: 13,
                        textDecoration: "none",
                      }}
                      title="Visit website"
                      aria-label={`${firm.name} website`}
                    >
                      🌐
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {connections.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
            Your connections ({connections.length})
          </h2>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>
            Firms you've reached out to. Reach back out anytime by clicking Connect again — your request will be re-saved.
          </p>
        </section>
      )}
    </div>
  );
};

function chipStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? "#1d4ed8" : "transparent",
    color: active ? "#fff" : "#475569",
    border: `1px solid ${active ? "#1d4ed8" : "#cbd5e1"}`,
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
}

export default StaffingFirms;
